/** @jsx jsx */

import { useState } from 'react';

import { Spacing } from '@zapier/theme';
import { copyTextToClipboard } from '@zapier/common-utils';
import {
  FloatingBox,
  Icon,
  Menu,
  MenuItem,
  Modal,
  ModalContent,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  TooltipWrapper,
} from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import Link from 'app/common/components/Link';
import MenuMoreToggler from 'app/common/components/MenuMoreToggler';
import { Status } from 'app/zap-templates/types';
import { useNotify } from 'app/developer-v3/hooks';
import { useDeleteZapTemplate } from 'app/zap-templates/hooks';
import {
  getZapTemplateEditUrl,
  getCreateTestZapUrl,
} from 'app/zap-templates/utils';

const Styles = {
  root: css`
    position: relative;
  `,
  itemWrapper: css`
    align-items: center;
    display: flex;
    gap: ${Spacing.small}px;
  `,
  link: css`
    color: inherit;
    text-decoration: none;
  `,
};

type Props = {
  id: string;
  landingPageUrl: string;
  status: Status;
  title: string;
};

function ActionsMenu({ id, landingPageUrl, status, title }: Props) {
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [isAboutDeleting, setIsAboutDeleting] = useState(false);

  const notify = useNotify();
  const {
    mutate: deleteZapTemplate,
    isMutating: isDeleting,
  } = useDeleteZapTemplate();

  const editUrl = getZapTemplateEditUrl(id);
  const createTestZapUrl = getCreateTestZapUrl(id);

  return (
    <span css={Styles.root}>
      <MenuMoreToggler
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpenMenu(!isOpenMenu);
        }}
      />
      {isOpenMenu && (
        <FloatingBox onClickOutside={() => setIsOpenMenu(false)}>
          <Menu aria-label="Manage Zap templates">
            <MenuItem
              onClick={event => {
                event.stopPropagation();
                setIsOpenMenu(false);
              }}
            >
              <Link css={Styles.link} href={editUrl.href} target="_blank">
                <span css={Styles.itemWrapper}>
                  <Icon name="actionEdit" size={20} />
                  Edit
                </span>
              </Link>
            </MenuItem>
            <TooltipWrapper
              content="Create Zap from template"
              isBlock={true}
              position="southeast"
            >
              {() => (
                <MenuItem
                  onClick={event => {
                    event.stopPropagation();
                    setIsOpenMenu(false);
                  }}
                >
                  <Link
                    css={Styles.link}
                    href={createTestZapUrl.href}
                    target="_blank"
                  >
                    <span css={Styles.itemWrapper}>
                      <Icon name="miscBoltAlt" size={20} />
                      Create Zap
                      <Icon name="alertInfo" size={16} />
                    </span>
                  </Link>
                </MenuItem>
              )}
            </TooltipWrapper>
            <TooltipWrapper
              allowMultilineTooltip={true}
              content="Copy a link for creating a Zap from the Template"
              isBlock={true}
              position="southeast"
            >
              {() => (
                <MenuItem
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    copyTextToClipboard(createTestZapUrl.href);
                    notify.success('Copied!');
                    setIsOpenMenu(false);
                  }}
                >
                  <span css={Styles.itemWrapper}>
                    <Icon name="actionLink" size={20} />
                    Copy Link
                    <Icon name="alertInfo" size={16} />
                  </span>
                </MenuItem>
              )}
            </TooltipWrapper>
            <MenuItem
              onClick={event => {
                event.stopPropagation();
                setIsOpenMenu(false);
              }}
            >
              <Link css={Styles.link} href={landingPageUrl} target="_blank">
                <span css={Styles.itemWrapper}>
                  <Icon name="fileGuide" size={20} />
                  View Landing Page{' '}
                </span>
              </Link>
            </MenuItem>
            <MenuItem
              onClick={event => {
                event.preventDefault();
                copyTextToClipboard(id);
                notify.success('Copied!');
                setIsOpenMenu(false);
              }}
            >
              <span css={Styles.itemWrapper}>
                <Icon name="actionCopy" size={20} />
                Copy ID ({id})
              </span>
            </MenuItem>
            <MenuItem
              disabled={isDeleting}
              onClick={event => {
                event.preventDefault();
                setIsOpenMenu(false);
                setIsAboutDeleting(true);
              }}
            >
              <span css={Styles.itemWrapper}>
                <Icon name="actionTrash" size={20} />
                Delete
              </span>
            </MenuItem>
          </Menu>
        </FloatingBox>
      )}
      {isAboutDeleting && (
        <Modal onClosed={() => setIsAboutDeleting(false)}>
          <ModalContent>
            <ModalContentHeader>Delete Zap template</ModalContentHeader>
            <ModalContentBody>
              Are you sure you want to delete the Zap template? ({title || id})
            </ModalContentBody>
            <ModalContentFooter>
              <AsyncButton
                color="danger"
                hasShadow={true}
                isWorking={isDeleting}
                onClick={async event => {
                  event.preventDefault();
                  await deleteZapTemplate({ id, status });
                  setIsAboutDeleting(false);
                }}
                size="small"
              >
                Yes, delete
              </AsyncButton>
            </ModalContentFooter>
          </ModalContent>
        </Modal>
      )}
    </span>
  );
}

export default ActionsMenu;
